import TailwindSideModal from "../../../components/commonModal/TailwindSideModal";
import TailwindBottomButtonView from "../../../components/mainBottomButtonView/TailwindBottomButtonView";
import { Card, CardContent, CardHeader } from "../../../components/ui/common/card";
import { Checkbox } from "../../../components/ui/common/checkbox";
import { Label } from "../../../components/ui/common/label";
import CommonSplitContainer from "../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import Field from "./components/Field";
import SupplierIngredientTable from "./components/SupplierIngredientTable";

const EYEBROW_CLASS =
  "tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground tw-font-medium";

interface Supplier {
  _id?: string;
  supplier_name?: string;
  supplier_contact_name?: string;
  supplier_email?: string;
  supplier_phone?: string;
  supplier_address?: string;
  supplier_province?: string;
  supplier_country?: number;
  supplier_postal_code?: string;
  supplier_other_info?: string;
  is_supplier_as_user?: boolean;
  ingredients?: any[];
  supplierIngredientDetails?: any[];
}

interface SupplierDetailsModalProps {
  visibleModal: boolean;
  setVisibleModal: React.Dispatch<React.SetStateAction<boolean>>;
  supplier: Supplier | any;
  countriesData: any;
  setVisibleEdit: any;
}

/**
 * Tailwind rebuild of the legacy `SupplierDetailsModal`
 * (src/screens/supplier/supplierDetails/SupplierDetailsModal.tsx), following
 * the Lovable prototype's read-only `/lca-suppliers` drawer: same shell/
 * width as the new edit drawer, same field order via a read-only `Field`
 * (label + value, "—" for empty) instead of disabled inputs. Same external
 * props as the legacy modal — drop-in swap behind
 * `ENABLE_LCA_SUPPLIER_UI_REDESIGN` in `SupplierList.tsx`.
 *
 * The linked-ingredients table (`SupplierIngredientList`) isn't part of the
 * prototype's design at all, but is kept (per product decision) since
 * removing existing functionality is out of scope for a visual-parity pass
 * — reused untouched from the legacy folder, just wrapped in a matching card.
 */
const SupplierDetailsModal = ({
  visibleModal,
  setVisibleModal,
  supplier,
  countriesData,
  setVisibleEdit = () => {}
}: SupplierDetailsModalProps) => {
  const countryName = countriesData?.find(
    (c: any) => c.key === (supplier?.supplier_country || 0)
  )?.text;

  const ingredients = supplier?.ingredients || [];
  const ingredientDetails = supplier?.supplierIngredientDetails || [];
  const supplierAsUser = supplier?.is_supplier_as_user || false;

  return (
    <TailwindSideModal
      open={visibleModal}
      onOpenChange={open => {
        if (!open) setVisibleModal(false);
      }}
      eyebrow="Suppliers"
      title={supplier?.supplier_name || "Supplier"}
      titleClassName="tw-font-display"
    >
      <CommonSplitContainer
        currentHeight={0.9}
        overflowX="hidden"
        topChild={
          <div className="tw-flex tw-flex-col tw-gap-6 tw-bg-muted/20 tw-p-6">
            <Card className="tw-gap-4 tw-p-5">
              <CardHeader className="tw-p-0">
                <span className={EYEBROW_CLASS}>Supplier info</span>
              </CardHeader>
              <CardContent className="tw-grid tw-grid-cols-1 tw-gap-4 tw-p-0 sm:tw-grid-cols-2">
                <Field label="Supplier Name" value={supplier?.supplier_name} />
                <Field label="Supplier Contact Name" value={supplier?.supplier_contact_name} />
                <Field label="Email" value={supplier?.supplier_email} />
                <Field label="Contact Number" value={supplier?.supplier_phone} mono />
              </CardContent>
            </Card>

            <Card className="tw-gap-4 tw-p-5">
              <CardHeader className="tw-p-0">
                <span className={EYEBROW_CLASS}>Location</span>
              </CardHeader>
              <CardContent className="tw-grid tw-grid-cols-1 tw-gap-4 tw-p-0 sm:tw-grid-cols-2">
                <Field label="Address" value={supplier?.supplier_address} />
                <div className="tw-flex tw-flex-col tw-gap-4">
                  <Field label="Country" value={countryName} />
                  <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                    <Field label="Province" value={supplier?.supplier_province} />
                    <Field label="Zip Code" value={supplier?.supplier_postal_code} mono />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="tw-gap-4 tw-p-5">
              <CardHeader className="tw-p-0">
                <span className={EYEBROW_CLASS}>Additional</span>
              </CardHeader>
              <CardContent className="tw-flex tw-flex-col tw-gap-4 tw-p-0">
                <Field label="Other Information" value={supplier?.supplier_other_info} />
                <div className="tw-flex tw-items-center tw-gap-2.5 tw-pt-1">
                  <Checkbox checked={supplierAsUser} disabled />
                  <Label className="tw-text-xs tw-font-normal">Register supplier as user</Label>
                </div>
              </CardContent>
            </Card>

            {ingredients.length > 0 ? (
              <SupplierIngredientTable
                ingredients={ingredients}
                ingredientDetails={ingredientDetails}
              />
            ) : null}
          </div>
        }
        bottomChild={
          <TailwindBottomButtonView
            cancelButtonStatus
            saveButtonStatus
            cancelButtonTitle="Close"
            saveTitle="Edit supplier"
            buttonSize="lg"
            cancelButton={() => setVisibleModal(false)}
            saveButton={() => setVisibleEdit(true)}
          />
        }
      />
    </TailwindSideModal>
  );
};

export default SupplierDetailsModal;
